// Pure rules behind the Costing & Recipes sheet (F-FE-N157 / F-MOD-N11).
//
// Everything that decides what a recipe line MEANS lives here, tested, so the
// editor component only renders. The failure modes on record that these rules
// close (audit/RECIPES_COSTING_OVERHAUL_2026-09-14.md,
// audit/RECIPE_BUILDER_AUDIT_2026-09-14.md):
//
//   * qty and unit both defaulted to the item's BULK unit, so "130" under
//     "liter" was one keystroke away → defaultUnitFor() picks the smallest unit
//     of the family and newLine() starts the quantity EMPTY.
//   * a single line costing more than the item sells for was saved and only
//     flagged later → lineGuard() blocks the save. It names BOTH causes (a
//     wrong unit, or a wrong ingredient cost) and never applies a fix on its
//     own: on demo, Karak Chai's milk quantity was right and the milk's cost was
//     wrong, and a one-click "Use ml" would have saved 1,000× too little milk.
//   * a row with an ingredient but no quantity, or a duplicate row, vanished on
//     save → rowGuards() blocks both on the row itself.
//   * a cup-only recipe read as healthy → packagingOnly() keeps the 25% rule.
//
// Quantities on screen are for the BATCH the operator authored (a whole cake);
// the server stores per serving. buildRecipePayload divides by the yield,
// fromServerComponents multiplies back.

import { canonicalUnit, convertUnits, getCompatibleUnits } from '@/lib/inventory-units'
import type { ProductCostingHealth, RecipeComponentView } from '@/types'

export type RecipeScope = 'all' | 'dine_in' | 'takeaway'
export type RecipeLineMode = 'add' | 'replace'

export const SCOPE_LABELS: Record<RecipeScope, string> = {
  all: 'Always',
  takeaway: 'Takeaway & delivery',
  dine_in: 'Dine-in only',
}

export const MODE_LABELS: Record<RecipeLineMode, string> = {
  add: 'Adds',
  replace: 'Replaces',
}

/** What the editor needs to know about a stock item. */
export interface StockItemRef {
  id: string
  name: string
  unit: string
  /** COALESCE(wac, default) — the rate recipes price at. null = uncosted. */
  unitCost: number | null
  supplierName?: string
  /** unitCost is a receipt average, which a typed cost never replaces (W-1). */
  costFromReceipts?: boolean
}

export interface RecipeLineDraft {
  /** Stable React key, independent of the stock item (which can change). */
  key: string
  stock_item_id: string
  /** As typed, for the batch. Empty until the operator types. */
  qty: string
  unit: string
  scope: RecipeScope
  /** Option recipes only; ignored for product recipes. */
  mode: RecipeLineMode
}

let keySeq = 0
function nextKey(): string {
  keySeq += 1
  return `rl-${Date.now().toString(36)}-${keySeq}`
}

/** The smallest unit in the stock unit's family — never the bulk unit. */
export function defaultUnitFor(stockUnit: string): string {
  const family = getCompatibleUnits(stockUnit)
  if (family.includes('g')) return 'g'
  if (family.includes('ml')) return 'ml'
  if (family.includes('each')) return 'each'
  return stockUnit
}

/** The largest unit in the stock unit's family — what an invoice is priced in. */
export function largestUnitFor(stockUnit: string): string {
  const family = getCompatibleUnits(stockUnit)
  if (family.includes('kg')) return 'kg'
  if (family.includes('liter')) return 'liter'
  if (family.includes('dozen')) return 'dozen'
  return stockUnit
}

/** A smaller unit in the same family, for the unit half of the cost guard. */
export function suggestSmallerUnit(unit: string): string | null {
  switch (unit) {
    case 'kg':
    case 'lb':
    case 'oz':
      return 'g'
    case 'liter':
      return 'ml'
    case 'dozen':
    case 'pair':
      return 'each'
    default:
      return null
  }
}

export function newLine(item?: StockItemRef, overrides: Partial<RecipeLineDraft> = {}): RecipeLineDraft {
  return {
    key: nextKey(),
    stock_item_id: item?.id ?? '',
    qty: '',
    unit: item ? defaultUnitFor(item.unit) : '',
    scope: 'all',
    mode: 'add',
    ...overrides,
  }
}

/** Quantity in the stock unit, or null when the unit cannot convert. */
export function toStockQty(qty: number, unit: string, stockUnit: string): number | null {
  if (!Number.isFinite(qty)) return null
  if (!unit || unit === stockUnit) return qty
  return convertUnits(qty, unit, stockUnit)
}

/**
 * The per-STOCK-unit cost for a price the owner typed per `enteredUnit`
 * ("Rs 300 per liter" on an ml-stocked item → 0.3 per ml). null when the
 * units don't convert.
 */
export function costPerStockUnit(price: number, enteredUnit: string, stockUnit: string): number | null {
  if (!Number.isFinite(price)) return null
  const f = enteredUnit === stockUnit ? 1 : convertUnits(1, enteredUnit, stockUnit)
  if (f == null || !(f > 0)) return null
  return price / f
}

/** A per-stock-unit rate expressed per `unit` (0.3 per ml → 300 per liter). */
export function rateInUnit(ratePerStockUnit: number, stockUnit: string, unit: string): number | null {
  const f = unit === stockUnit ? 1 : convertUnits(1, unit, stockUnit)
  if (f == null) return null
  return ratePerStockUnit * f
}

export interface LineFigures {
  /** Per SERVING, in the stock unit. */
  perServingQty: number
  /** Per serving at the item's rate; 0 when uncosted. */
  cost: number
  costed: boolean
}

/** Per-serving quantity and cost of one line. null = blank or unconvertible. */
export function lineFigures(line: RecipeLineDraft, item: StockItemRef | undefined, yieldN: number): LineFigures | null {
  if (!item || !line.stock_item_id) return null
  const typed = Number(line.qty)
  if (!(typed > 0)) return null
  const y = yieldN > 0 ? yieldN : 1
  const stockQty = toStockQty(typed, line.unit, item.unit)
  if (stockQty == null) return null
  const perServingQty = stockQty / y
  const rate = item.unitCost
  return {
    perServingQty,
    cost: rate != null ? perServingQty * rate : 0,
    costed: rate != null,
  }
}

export type LineGuardKind = 'cost' | 'blank' | 'duplicate' | 'unit'

export interface LineGuard {
  level: 'block' | 'warn'
  kind: LineGuardKind
  message: string
  /**
   * Cost guard only: what the line would become in a smaller unit. Shown on
   * the button BEFORE it is applied ("Change to 0.2 ml (Rs 20)"), because the
   * quantity may be right and the ingredient's cost wrong.
   */
  swap?: { unit: string; qty: string; cost: number | null }
  /** Cost guard only: the ingredient whose cost may be the real fault. */
  costItemId?: string
}

/**
 * The NOOK predicate at the keyboard: a line that costs more per serving than
 * the item sells for is wrong, and Save is blocked. Two things can be wrong —
 * the quantity's unit, or the ingredient's cost — and a screen cannot tell
 * which, so the message names both and offers both fixes without applying one.
 */
export function lineGuard(
  line: RecipeLineDraft,
  item: StockItemRef | undefined,
  price: number,
  yieldN: number,
  formatMoney: (n: number) => string,
): LineGuard | null {
  const f = lineFigures(line, item, yieldN)
  if (!f || !item || !f.costed || !(price > 0) || !(f.cost > price)) return null
  const rate = item.unitCost ?? 0
  const larger = largestUnitFor(item.unit)
  const largerRate = larger !== item.unit ? rateInUnit(rate, item.unit, larger) : null
  const rateText =
    `${formatMoney(rate)} per ${item.unit}` +
    (largerRate != null ? ` = ${formatMoney(largerRate)} per ${larger}` : '')

  let swap: LineGuard['swap']
  const smaller = suggestSmallerUnit(line.unit)
  if (smaller) {
    const swapped = lineFigures({ ...line, unit: smaller }, item, yieldN)
    if (swapped) swap = { unit: smaller, qty: line.qty, cost: swapped.costed ? swapped.cost : null }
  }

  return {
    level: 'block',
    kind: 'cost',
    message:
      `${item.name}: ${line.qty} ${line.unit} per serving costs ${formatMoney(f.cost)} — more than the item sells for (${formatMoney(price)}). ` +
      `Either the quantity is in the wrong unit, or ${item.name}'s cost is wrong (${rateText}).`,
    swap,
    costItemId: item.id,
  }
}

/**
 * Every guard on every row, keyed by row key. Fully empty rows (no ingredient,
 * no quantity) are ignored — they are what "Add ingredient" leaves behind and
 * the payload drops them. Everything else that the payload would silently drop
 * or merge is a blocking guard on the row itself.
 */
export function rowGuards(
  lines: RecipeLineDraft[],
  items: Record<string, StockItemRef>,
  price: number,
  yieldN: number,
  formatMoney: (n: number) => string,
  kind: 'product' | 'option',
): Record<string, LineGuard[]> {
  const out: Record<string, LineGuard[]> = {}
  const seen = new Set<string>()
  for (const l of lines) {
    const guards: LineGuard[] = []
    const hasItem = Boolean(l.stock_item_id)
    const qtyTyped = l.qty.trim() !== ''
    const qtyValid = Number(l.qty) > 0
    if (!hasItem && !qtyTyped) {
      out[l.key] = guards
      continue
    }
    const item = items[l.stock_item_id]
    if (hasItem && !qtyValid) {
      guards.push({ level: 'block', kind: 'blank', message: 'Enter a quantity or remove this row.' })
    } else if (!hasItem) {
      guards.push({ level: 'block', kind: 'blank', message: 'Pick an ingredient or remove this row.' })
    }
    if (item && qtyValid && toStockQty(Number(l.qty), l.unit, item.unit) == null) {
      guards.push({
        level: 'block',
        kind: 'unit',
        message: `${item.name} is stocked in ${item.unit}, and ${l.unit || 'that unit'} can't be converted to it. Pick a unit from the list.`,
      })
    }
    if (hasItem) {
      const dupKey = `${l.stock_item_id}|${l.scope}|${kind === 'option' ? l.mode : ''}`
      if (seen.has(dupKey)) {
        guards.push({
          level: 'block',
          kind: 'duplicate',
          message: `${item?.name ?? 'This ingredient'} is already on this recipe for ${SCOPE_LABELS[l.scope]}${kind === 'option' ? ` (${MODE_LABELS[l.mode]})` : ''}. Change the quantity on that row, or remove this one.`,
        })
      } else {
        seen.add(dupKey)
      }
    }
    const cost = lineGuard(l, item, price, yieldN, formatMoney)
    if (cost) guards.push(cost)
    out[l.key] = guards
  }
  return out
}

/** Every blocking guard on the current lines — the sheet disables Save on any. */
export function collectBlocks(
  lines: RecipeLineDraft[],
  items: Record<string, StockItemRef>,
  price: number,
  yieldN: number,
  formatMoney: (n: number) => string,
  kind: 'product' | 'option' = 'product',
): LineGuard[] {
  const all = rowGuards(lines, items, price, yieldN, formatMoney, kind)
  return Object.values(all).flat().filter((g) => g.level === 'block')
}

export interface RecipeTotals {
  cost: number
  uncosted: number
  lines: number
  profit: number
  marginPct: number
  loss: boolean
}

const SCOPE_FOR: Record<'dine_in' | 'takeaway', RecipeScope[]> = {
  dine_in: ['all', 'dine_in'],
  takeaway: ['all', 'takeaway'],
}

/** Per-serving totals for one order-type scope (dine-in is what the list shows). */
export function recipeTotals(
  lines: RecipeLineDraft[],
  items: Record<string, StockItemRef>,
  yieldN: number,
  price: number,
  scope: 'dine_in' | 'takeaway' = 'dine_in',
): RecipeTotals {
  let cost = 0
  let uncosted = 0
  let n = 0
  for (const l of lines) {
    if (!SCOPE_FOR[scope].includes(l.scope)) continue
    const f = lineFigures(l, items[l.stock_item_id], yieldN)
    if (!f) continue
    n += 1
    if (!f.costed) uncosted += 1
    cost += f.cost
  }
  const p = Number(price) || 0
  const profit = p - cost
  return {
    cost,
    uncosted,
    lines: n,
    profit,
    marginPct: p > 0 ? (profit / p) * 100 : 0,
    loss: p > 0 && cost > p,
  }
}

/** A recipe this much cheaper than the owner's own figure is usually missing its main ingredients. */
export const PACKAGING_ONLY_RATIO = 0.25

export function packagingOnly(recipeCost: number, manualEstimate: number | null | undefined): boolean {
  return (
    recipeCost > 0 &&
    manualEstimate != null &&
    manualEstimate > 0 &&
    recipeCost < manualEstimate * PACKAGING_ONLY_RATIO
  )
}

export interface RecipeComponentPayloadLine {
  stock_item_id: string
  deduct_qty_entered: number
  deduct_unit: string
  order_type_scope: RecipeScope
  mode?: RecipeLineMode
}

/**
 * The server payload: per-serving entered quantities (batch ÷ yield). Blank
 * rows are dropped; duplicates of the same (item, scope[, mode]) keep the
 * first. Option recipes carry the mode; product recipes omit it. The sheet
 * blocks Save on anything this would drop (rowGuards), so dropping here is a
 * backstop, not a behaviour the owner can hit.
 */
export function buildRecipePayload(
  lines: RecipeLineDraft[],
  yieldN: number,
  kind: 'product' | 'option',
): RecipeComponentPayloadLine[] {
  const y = yieldN > 0 ? yieldN : 1
  const seen = new Set<string>()
  const out: RecipeComponentPayloadLine[] = []
  for (const l of lines) {
    const typed = Number(l.qty)
    if (!l.stock_item_id || !(typed > 0)) continue
    const k = `${l.stock_item_id}|${l.scope}|${kind === 'option' ? l.mode : ''}`
    if (seen.has(k)) continue
    seen.add(k)
    out.push({
      stock_item_id: l.stock_item_id,
      deduct_qty_entered: round5(typed / y),
      deduct_unit: l.unit,
      order_type_scope: l.scope,
      ...(kind === 'option' ? { mode: l.mode } : {}),
    })
  }
  return out
}

/** Server rows → editor lines, quantities multiplied back to the batch. */
export function fromServerComponents(components: RecipeComponentView[], yieldN: number): RecipeLineDraft[] {
  const y = yieldN > 0 ? yieldN : 1
  return components.map((c) => {
    // F-FE-N165: fold import spellings (`pc` → `each`) so the unit is one the
    // select can show and the converter understands.
    let unit = canonicalUnit(c.deduct_unit || c.unit)
    let perServing = c.deduct_qty_entered ?? null
    if (perServing == null) {
      // Legacy row: only the stock-unit quantity exists. Show it in the unit
      // it was authored under when that converts; otherwise in the stock unit.
      perServing = c.deduct_qty
      if (unit !== canonicalUnit(c.unit)) {
        const conv = convertUnits(c.deduct_qty, c.unit, unit)
        if (conv != null) {
          perServing = conv
        } else {
          // The label never converted (NinetyPlus tea bags: "g" on an item
          // stocked per bag). deduct_qty IS in the stock unit — show it there,
          // as the comment above always promised, instead of "1 g".
          unit = canonicalUnit(c.unit)
        }
      }
    }
    return {
      key: nextKey(),
      stock_item_id: c.stock_item_id,
      qty: String(round5(perServing * y)),
      unit,
      scope: (c.order_type_scope as RecipeScope) || 'all',
      mode: (c.mode as RecipeLineMode) || 'add',
    }
  })
}

export function round5(n: number): number {
  return Math.round(n * 100000) / 100000
}

// ── List status ─────────────────────────────────────────────────────────────

export type CostingStatus = 'none' | 'recipe' | 'fixed' | 'percent' | 'attention'

export const COSTING_STATUS_LABELS: Record<CostingStatus, string> = {
  none: 'No cost',
  recipe: 'Recipe',
  fixed: 'Fixed',
  percent: '% of price',
  attention: 'Needs attention',
}

export interface CostingStatusSource {
  projected_cost?: number | null
  projected_cost_basis?: 'amount' | 'percent' | null
}

/** Which rung is in force, with "attention" winning whenever a flag fires. */
export function costingStatus(p: CostingStatusSource, h: ProductCostingHealth | undefined): CostingStatus {
  if (h && h.component_count > 0) {
    if (h.components_missing_cost > 0) return 'attention'
    if (h.recipe_cost > 0 && h.price > 0 && h.recipe_cost > h.price) return 'attention'
    if (packagingOnly(h.recipe_cost, h.projected_cost)) return 'attention'
    return 'recipe'
  }
  if (h?.effective_cost_source === 'consignment') return 'fixed'
  if (p.projected_cost_basis === 'percent') return 'percent'
  if (p.projected_cost != null) return 'fixed'
  return 'none'
}

// ── Variants (option recipes) ───────────────────────────────────────────────

export interface VariantDraft {
  /** As typed; 1 = no scaling. */
  multiplier: string
  lines: RecipeLineDraft[]
  /**
   * F-MOD-N13: save this recipe for THIS item only, leaving the shared
   * option recipe (and every other item on the list) untouched.
   */
  onlyThisItem?: boolean
}

/** A multiplier the server will accept: a number above 0. Blank is not 1. */
export function multiplierValid(m: string): boolean {
  return m.trim() !== '' && Number(m) > 0
}

/**
 * Dine-in per-serving cost of the product WITH one option chosen — the
 * client mirror of the engine's combine rule (recipe_resolve.go): the
 * multiplier scales base and replace lines, replace overrides the base line
 * for the same item, adds are extra, scale by the option's quantity on the
 * line, and are never multiplied. When `uncosted > 0` the figure is a floor,
 * not a cost — callers must not show a margin from it.
 */
export function variantCost(
  base: RecipeLineDraft[],
  yieldN: number,
  draft: VariantDraft,
  items: Record<string, StockItemRef>,
  price: number,
  optionQty = 1,
): RecipeTotals {
  const mult = Number(draft.multiplier) > 0 ? Number(draft.multiplier) : 1
  const q = optionQty >= 1 ? optionQty : 1
  const perItem = new Map<string, { qty: number; costed: boolean; rate: number }>()
  for (const l of base) {
    if (!SCOPE_FOR.dine_in.includes(l.scope)) continue
    const f = lineFigures(l, items[l.stock_item_id], yieldN)
    if (!f) continue
    const cur = perItem.get(l.stock_item_id) ?? { qty: 0, costed: f.costed, rate: items[l.stock_item_id]?.unitCost ?? 0 }
    cur.qty += f.perServingQty * mult
    perItem.set(l.stock_item_id, cur)
  }
  for (const l of draft.lines) {
    if (l.mode !== 'replace' || !SCOPE_FOR.dine_in.includes(l.scope)) continue
    const f = lineFigures(l, items[l.stock_item_id], 1)
    if (!f) continue
    perItem.set(l.stock_item_id, { qty: f.perServingQty * mult, costed: f.costed, rate: items[l.stock_item_id]?.unitCost ?? 0 })
  }
  for (const l of draft.lines) {
    if (l.mode === 'replace' || !SCOPE_FOR.dine_in.includes(l.scope)) continue
    const f = lineFigures(l, items[l.stock_item_id], 1)
    if (!f) continue
    const cur = perItem.get(l.stock_item_id) ?? { qty: 0, costed: f.costed, rate: items[l.stock_item_id]?.unitCost ?? 0 }
    cur.qty += f.perServingQty * q
    perItem.set(l.stock_item_id, cur)
  }
  let cost = 0
  let uncosted = 0
  for (const v of perItem.values()) {
    if (!v.costed) uncosted += 1
    cost += v.qty * v.rate
  }
  const p = Number(price) || 0
  const profit = p - cost
  return { cost, uncosted, lines: perItem.size, profit, marginPct: p > 0 ? (profit / p) * 100 : 0, loss: p > 0 && cost > p }
}

// -- Setting an ingredient's cost (walkthrough W-1) --------------------------

/**
 * How an ingredient's rate can be fixed from a recipe. Once costed stock has
 * been received, recipes price at the moving average and a typed price never
 * replaces it (F-RPT-N20), so the fix is correcting the receipt, not a new figure.
 */
export function costFixPath(item: Pick<StockItemRef, 'unitCost' | 'costFromReceipts'>): 'type' | 'receipts' {
  return item.unitCost != null && item.costFromReceipts ? 'receipts' : 'type'
}

/**
 * After "Save cost": did the rate recipes use become what was typed?
 * `effective` is the item's rate re-read from the server; unknown when it
 * could not be read (never claim an effect we did not see).
 */
export function costSaveOutcome(entered: number, effective: number | null | undefined): 'applied' | 'average_kept' | 'unknown' {
  if (effective == null) return 'unknown'
  return Math.abs(effective - entered) > Math.max(0.005, Math.abs(entered) * 0.001) ? 'average_kept' : 'applied'
}
